import { useEffect, useRef, useState } from "react";

/**
 * useLiveCalls escucha los eventos de llamada según pasan.
 *
 * # Por qué SSE y no WebSocket
 *
 * El tráfico va en un solo sentido: la plataforma cuenta y el portal escucha.
 * `EventSource` es HTTP normal —misma cookie, mismo proxy— y **reconecta
 * solo**, que es justo la parte que con un WebSocket habría que escribir a
 * mano y mantener.
 *
 * # Por qué el sondeo no desaparece
 *
 * Porque este canal puede no estar: un despliegue sin el rol que lo sirve, un
 * proxy que acumula, una red que lo corta. La pantalla sigue refrescando sola,
 * solo que más despacio mientras el canal esté vivo. Un aviso que depende de
 * que todo funcione no es un aviso.
 */
export type EstadoDelCanal = "conectando" | "en-vivo" | "sin-canal";

export function useLiveCalls(onEvent: (tipo: string, data: Record<string, unknown>) => void): {
  estado: EstadoDelCanal;
  /** Sube cuando el servidor dice que se perdió más de lo que puede reenviar. */
  desfase: number;
} {
  const [estado, setEstado] = useState<EstadoDelCanal>("conectando");
  const [desfase, setDesfase] = useState(0);

  // El callback va en una ref: cambia en cada render de la pantalla, y
  // reabrir el canal por eso sería reconectar varias veces por segundo.
  const manejador = useRef(onEvent);
  useEffect(() => {
    manejador.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (typeof EventSource === "undefined") {
      setEstado("sin-canal");
      return;
    }

    const fuente = new EventSource("/api/v1/calls/stream", { withCredentials: true });

    fuente.onopen = () => setEstado("en-vivo");

    // `EventSource` reintenta solo, así que un error no es terminal: puede ser
    // un corte de un segundo. Lo que se marca es que ahora mismo no hay canal,
    // y la pantalla vuelve a su ritmo de sondeo mientras tanto.
    fuente.onerror = () => {
      setEstado(fuente.readyState === EventSource.CLOSED ? "sin-canal" : "conectando");
    };

    // Los tipos se escuchan uno a uno y no con `onmessage` porque el servidor
    // los manda con `event:`, y un `message` genérico no los recibiría.
    const tipos = [
      "call.queued",
      "call.initiated",
      "call.progressing",
      "call.ringing",
      "call.answered",
      "call.completed",
      "call.failed",
    ];
    const escuchas = tipos.map((tipo) => {
      const escucha = (e: MessageEvent) => {
        try {
          const sobre = JSON.parse(e.data) as { data?: Record<string, unknown> };
          manejador.current(tipo, sobre.data ?? {});
        } catch {
          // Un mensaje que no es JSON no debe tirar el canal entero.
        }
      };
      fuente.addEventListener(tipo, escucha);
      return { tipo, escucha };
    });

    // El servidor avisa cuando se perdió más de lo que puede reenviar: ahí la
    // pantalla recarga en lugar de quedarse con una lista a medias.
    const desfasado = () => setDesfase((n) => n + 1);
    fuente.addEventListener("desfase", desfasado);

    return () => {
      for (const { tipo, escucha } of escuchas) fuente.removeEventListener(tipo, escucha);
      fuente.removeEventListener("desfase", desfasado);
      fuente.close();
    };
  }, []);

  return { estado, desfase };
}
